import React, { useEffect } from "react";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import helperServices from "../../../shared/helper/helper";
pdfMake.vfs = pdfFonts.pdfMake.vfs;

function PrintPainAssessment(props) {
  const { patientInfo, regDate } = props;

  const name =
    patientInfo?.fullName ||
    [patientInfo?.fname, patientInfo?.mname, patientInfo?.lname]
      .filter(Boolean)
      .join(" ") ||
    "—";

  const field = (val) => val || "—";

  // ─────────────────────────────────────────────
  // Create pain scale image with selected score
  // visually marked on the existing image.
  // ─────────────────────────────────────────────
  const createMarkedPainScaleImage = () =>
    new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement("canvas");

        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;

        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error("Unable to create canvas context"));
          return;
        }

        // Draw the original pain scale image first.
        ctx.drawImage(img, 0, 0);

        // Get selected pain score.
        const score = Math.max(
          0,
          Math.min(10, Number(patientInfo?.painScore) || 0)
        );

        // ─────────────────────────────────────────
        // Temporary scale coordinates.
        // These will be calibrated after checking the
        // actual pain_assesment.jpg image.
        // ─────────────────────────────────────────
        const scaleX1 = 152;
        const scaleX2 = 1416;

        const markerX =
          scaleX1 + (score / 10) * (scaleX2 - scaleX1);   

        const markerY = 534;   

        // ─────────────────────────────────────────
        // Draw marker
        // ─────────────────────────────────────────
        ctx.save(); 

        ctx.strokeStyle = "#000000";
        ctx.fillStyle = "#000000";
        ctx.lineWidth = Math.max(3, canvas.width / 160);

        // Vertical marker line
        ctx.beginPath();
        ctx.moveTo(markerX, markerY - 35);
        ctx.lineTo(markerX, markerY + 25);
        ctx.stroke();

        // Marker circle
        ctx.beginPath();
        ctx.arc(markerX, markerY - 40, 8, 0, Math.PI * 2);
        ctx.fill();

        // Score number
        ctx.font = "bold 24px Arial";
        ctx.textAlign = "center";
        ctx.fillText(String(score), markerX, markerY - 50);

        ctx.restore();

        // Convert marked canvas back to image.
        resolve(canvas.toDataURL("image/png"));
      };

      img.onerror = () => {
        reject(
          new Error("Unable to load pain_assesment.jpg")
        );
      };

      img.src =
        window.location.origin + "/pain_assesment.jpg";
    });

  useEffect(() => {
    props.parentCallBack();
    printForm();
  }, []);

  // ─────────────────────────────────────────────
  // Build PDF
  // ─────────────────────────────────────────────
  const buildDocDefinition = async () => {
    const doctorName = field(patientInfo?.doctorName);
    const ageSex = `${field(patientInfo?.age)} / ${field(
      patientInfo?.gender
    )}`;

    // Create the pain scale image with the selected
    // pain score visually marked.
    const markedPainScaleImg =
      await createMarkedPainScaleImage();

    // ─────────────────────────────────────────────
    // Existing pdfMake canvas code
    // ─────────────────────────────────────────────

    // Face positions (cx) for 6 faces at 0,2,4,6,8,10
    // Scale bar from x=50 to x=490, y=120
    // Faces at y=60

    const scaleX1 = 50;
    const scaleX2 = 490;
    const scaleY = 145;
    const faceY = 80;
    const faceR = 28;

    // 6 faces at positions 0,2,4,6,8,10 mapped to x
    const facePositions = [0, 2, 4, 6, 8, 10].map(
      (v) =>
        scaleX1 +
        (v / 10) * (scaleX2 - scaleX1)
    );

    // Colors per face
    const faceColors = [
      "#2ecc40",
      "#5cb85c",
      "#f1c40f",
      "#e67e22",
      "#c0392b",
      "#922b21",
    ];

    const labels = [
      "no pain",
      "mild",
      "moderate",
      "severe",
      "very\nsevere",
      "worst",
    ];

    const numbers = [
      "0",
      "2",
      "4",
      "6",
      "8",
      "10",
    ];

    // Build canvas shapes
    const canvasShapes = [];

    // Background box
    canvasShapes.push({
      type: "rect",
      x: 20,
      y: 10,
      w: 500,
      h: 195,
      r: 8,
      color: "#e8f8f5",
      lineColor: "#a9cce3",
      lineWidth: 1,
    });

    // Title
    // (handled as text above canvas)

    // Draw scale line
    canvasShapes.push({
      type: "line",
      x1: scaleX1,
      y1: scaleY,
      x2: scaleX2,
      y2: scaleY,
      lineWidth: 3,
      lineColor: "#555555",
    });

    // Draw tick marks and number labels for 0-10
    for (let i = 0; i <= 10; i++) {
      const tx =
        scaleX1 +
        (i / 10) * (scaleX2 - scaleX1);

      canvasShapes.push({
        type: "line",
        x1: tx,
        y1: scaleY - 6,
        x2: tx,
        y2: scaleY + 6,
        lineWidth: i % 2 === 0 ? 2 : 1,
        lineColor: "#555555",
      });
    }

    // Draw faces
    facePositions.forEach((cx, idx) => {
      const color = faceColors[idx];

      // Face circle
      canvasShapes.push({
        type: "ellipse",
        x: cx,
        y: faceY,
        r1: faceR,
        r2: faceR,
        color: color,
        lineColor: "#333333",
        lineWidth: 1,
      });

      // Left eye
      canvasShapes.push({
        type: "ellipse",
        x: cx - 9,
        y: faceY - 8,
        r1: 3,
        r2: 3,
        color: "#1a1a1a",
      });

      // Right eye
      canvasShapes.push({
        type: "ellipse",
        x: cx + 9,
        y: faceY - 8,
        r1: 3,
        r2: 3,
        color: "#1a1a1a",
      });

      // Mouth
      if (idx === 0) {
        // Big smile
        canvasShapes.push({
          type: "line",
          x1: cx - 12,
          y1: faceY + 10,
          x2: cx,
          y2: faceY + 16,
          lineWidth: 2,
          lineColor: "#1a1a1a",
        });

        canvasShapes.push({
          type: "line",
          x1: cx,
          y1: faceY + 16,
          x2: cx + 12,
          y2: faceY + 10,
          lineWidth: 2,
          lineColor: "#1a1a1a",
        });
      } else if (idx === 1) {
        // Small smile
        canvasShapes.push({
          type: "line",
          x1: cx - 10,
          y1: faceY + 10,
          x2: cx,
          y2: faceY + 14,
          lineWidth: 2,
          lineColor: "#1a1a1a",
        });

        canvasShapes.push({
          type: "line",
          x1: cx,
          y1: faceY + 14,
          x2: cx + 10,
          y2: faceY + 10,
          lineWidth: 2,
          lineColor: "#1a1a1a",
        });
      } else if (idx === 2) {
        // Neutral
        canvasShapes.push({
          type: "line",
          x1: cx - 10,
          y1: faceY + 12,
          x2: cx + 10,
          y2: faceY + 12,
          lineWidth: 2,
          lineColor: "#1a1a1a",
        });
      } else if (idx === 3) {
        // Small frown
        canvasShapes.push({
          type: "line",
          x1: cx - 10,
          y1: faceY + 14,
          x2: cx,
          y2: faceY + 10,
          lineWidth: 2,
          lineColor: "#1a1a1a",
        });

        canvasShapes.push({
          type: "line",
          x1: cx,
          y1: faceY + 10,
          x2: cx + 10,
          y2: faceY + 14,
          lineWidth: 2,
          lineColor: "#1a1a1a",
        });
      } else if (idx === 4) {
        // Medium frown
        canvasShapes.push({
          type: "line",
          x1: cx - 12,
          y1: faceY + 16,
          x2: cx,
          y2: faceY + 10,
          lineWidth: 2,
          lineColor: "#1a1a1a",
        });

        canvasShapes.push({
          type: "line",
          x1: cx,
          y1: faceY + 10,
          x2: cx + 12,
          y2: faceY + 16,
          lineWidth: 2,
          lineColor: "#1a1a1a",
        });
      } else {
        // Big frown + tears
        canvasShapes.push({
          type: "line",
          x1: cx - 13,
          y1: faceY + 17,
          x2: cx,
          y2: faceY + 9,
          lineWidth: 2,
          lineColor: "#1a1a1a",
        });

        canvasShapes.push({
          type: "line",
          x1: cx,
          y1: faceY + 9,
          x2: cx + 13,
          y2: faceY + 17,
          lineWidth: 2,
          lineColor: "#1a1a1a",
        });

        // Tear drops
        canvasShapes.push({
          type: "ellipse",
          x: cx - 11,
          y: faceY - 2,
          r1: 2,
          r2: 3,
          color: "#3498db",
        });

        canvasShapes.push({
          type: "ellipse",
          x: cx + 11,
          y: faceY - 2,
          r1: 2,
          r2: 3,
          color: "#3498db",
        });
      }
    });

    return {
      pageSize: "A4",
      pageMargins: [40, 40, 40, 40],

      defaultStyle: {
        font: "Roboto",
      },

      images: {
        // Use the newly generated image with the
        // selected pain score marked.
        painScaleImg: markedPainScaleImg,
      },

      content: [
        // ─────────────────────────────────────────
        // Title
        // ─────────────────────────────────────────
        {
          text: "VISUAL ANALOG SCALE",
          fontSize: 16,
          bold: true,
          alignment: "center",
          margin: [0, 0, 0, 20],
        },

        // ─────────────────────────────────────────
        // Patient fields
        // ─────────────────────────────────────────
        {
          table: {
            widths: [140, "*"],

            body: [
              [
                {
                  text: "Patient Name",
                  bold: true,
                  fontSize: 11,
                  border: [
                    false,
                    false,
                    false,
                    false,
                  ],
                  margin: [0, 4, 0, 4],
                },
                {
                  text: `:  ${name}`,
                  fontSize: 11,
                  border: [
                    false,
                    false,
                    false,
                    false,
                  ],
                  margin: [0, 4, 0, 4],
                },
              ],

              [
                {
                  text: "Date and Time of Examination",
                  bold: true,
                  fontSize: 11,
                  border: [
                    false,
                    false,
                    false,
                    false,
                  ],
                  margin: [0, 4, 0, 4],
                },
                {
                  text: `:  ${field(
                    helperServices.formatDateNTime(regDate, true)
                  )}`,
                  fontSize: 11,
                  border: [
                    false,
                    false,
                    false,
                    false,
                  ],
                  margin: [0, 4, 0, 4],
                },
              ],

              [
                {
                  text: "Age / Sex",
                  bold: true,
                  fontSize: 11,
                  border: [
                    false,
                    false,
                    false,
                    false,
                  ],
                  margin: [0, 4, 0, 4],
                },
                {
                  text: `:  ${ageSex}`,
                  fontSize: 11,
                  border: [
                    false,
                    false,
                    false,
                    false,
                  ],
                  margin: [0, 4, 0, 4],
                },
              ],

              [
                {
                  text: "Name of the Doctor",
                  bold: true,
                  fontSize: 11,
                  border: [
                    false,
                    false,
                    false,
                    false,
                  ],
                  margin: [0, 4, 0, 4],
                },
                {
                  text: `:  ${doctorName}`,
                  fontSize: 11,
                  border: [
                    false,
                    false,
                    false,
                    false,
                  ],
                  margin: [0, 4, 0, 4],
                },
              ],

              [
                {
                  text: "Diagnosis",
                  bold: true,
                  fontSize: 11,
                  border: [
                    false,
                    false,
                    false,
                    false,
                  ],
                  margin: [0, 4, 0, 4],
                },
                {
                  text: `:  ${field(
                    patientInfo?.diagnosis
                  )}`,
                  fontSize: 11,
                  border: [
                    false,
                    false,
                    false,
                    false,
                  ],
                  margin: [0, 4, 0, 4],
                },
              ],
            ],
          },

          layout: "noBorders",

          margin: [0, 0, 0, 24],
        },

        // ─────────────────────────────────────────
        // Pain scale image
        // ─────────────────────────────────────────
        {
          image: "painScaleImg",
          width: 500,
          alignment: "center",
          margin: [0, 10, 0, 30],
        },

        // ─────────────────────────────────────────
        // Signature block
        // ─────────────────────────────────────────
        {
          table: {
            widths: [130, "*"],

            body: [
              [
                {
                  text: "Signature of Doctor",
                  bold: true,
                  fontSize: 11,
                  border: [
                    false,
                    false,
                    false,
                    false,
                  ],
                  margin: [0, 6, 0, 6],
                },
                patientInfo?.doctorSignature
                  ? {
                      image: patientInfo.doctorSignature,
                      width: 120,
                      height: 45,
                      border: [
                        false,
                        false,
                        false,
                        false,
                      ],
                      margin: [0, 0, 0, 0],
                    }
                  : {
                      text: ":  —",
                      fontSize: 11,
                      border: [
                        false,
                        false,
                        false,
                        false,
                      ],
                      margin: [0, 6, 0, 6],
                    },
              ],

              [
                {
                  text: "Date",
                  bold: true,
                  fontSize: 11,
                  border: [
                    false,
                    false,
                    false,
                    false,
                  ],
                  margin: [0, 6, 0, 6],
                },
                {
                  text: `:  ${
                    patientInfo?.doctorSignedAt
                      ? helperServices.formatDateOnly(
                          patientInfo.doctorSignedAt,
                          true
                        )
                      : "—"
                  }`,
                  fontSize: 11,
                  border: [
                    false,
                    false,
                    false,
                    false,
                  ],
                  margin: [0, 6, 0, 6],
                },
              ],

              [
                {
                  text: "Time",
                  bold: true,
                  fontSize: 11,
                  border: [
                    false,
                    false,
                    false,
                    false,
                  ],
                  margin: [0, 6, 0, 6],
                },
                {
                  text: `:  ${
                    patientInfo?.doctorSignedAt
                      ? helperServices.formatTimeOnly(
                          patientInfo.doctorSignedAt
                        )
                      : "—"
                  }`,
                  fontSize: 11,
                  border: [
                    false,
                    false,
                    false,
                    false,
                  ],
                  margin: [0, 6, 0, 6],
                },
              ],
            ],
          },

          layout: "noBorders",

          margin: [0, 30, 0, 0],
        },
      ],
    };
  } 

  // ─────────────────────────────────────────────
  // Print form
  // ─────────────────────────────────────────────
  const printForm = async () => {
    try {
      const docDefinition =
        await buildDocDefinition();

      pdfMake
        .createPdf(docDefinition)
        .getBlob((blob) => {
          const url = URL.createObjectURL(blob);

          window.open(
            `/Patient_Profile_Print?file=${encodeURIComponent(
              url
            )}`,
            "_blank"
          );
        });
    } catch (error) {
      console.error(
        "Pain Assessment PDF generation failed:",
        error
      );
    }
  };

  return null;
}

export default PrintPainAssessment;
